import React, { useState, useEffect } from "react";
import { useGetHomepageSettingsQuery } from "../redux/services/api";
import { useTranslation } from "../hooks/useTranslation";
import AOS from "aos";
import "aos/dist/aos.css";

// Helper function to parse Draft.js content to HTML
const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};

const IntroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const { t, isArabic } = useTranslation();

  // Use RTK Query to fetch homepage settings
  const { data: settingsResponse, isLoading: loading } =
    useGetHomepageSettingsQuery();

  const aboutData = {
    title: isArabic
      ? settingsResponse?.settings?.aboutUs?.titleAr ||
        settingsResponse?.settings?.aboutUs?.title ||
        "SILVER FORT"
      : settingsResponse?.settings?.aboutUs?.title || "SILVER FORT",
    description: isArabic
      ? settingsResponse?.settings?.aboutUs?.descriptionAr ||
        settingsResponse?.settings?.aboutUs?.description ||
        t("intro.description")
      : settingsResponse?.settings?.aboutUs?.description ||
        t("intro.description"),
    images: settingsResponse?.settings?.aboutUs?.images || [],
  };

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const handleDotClick = (index) => {
    setActiveSlide(index);
  };

  const handlePrevSlide = () => {
    setActiveSlide((prev) =>
      prev === 0 ? aboutData.images.length - 1 : prev - 1
    );
  };

  const handleNextSlide = () => {
    setActiveSlide((prev) =>
      prev === aboutData.images.length - 1 ? 0 : prev + 1
    );
  };

  // Auto-slide functionality
  useEffect(() => {
    if (aboutData.images.length <= 1) return;

    const interval = setInterval(() => {
      setActiveSlide((prev) =>
        prev === aboutData.images.length - 1 ? 0 : prev + 1
      );
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [aboutData.images.length]);

  const renderImageSlider = () => {
    console.log("Rendering slider with images:", aboutData.images.length);

    if (aboutData.images.length === 0) {
      return (
        <div className="showcase-placeholder">
          <div className="placeholder-content">
            <div className="placeholder-icon">📸</div>
            <p>No images available</p>
          </div>
        </div>
      );
    }

    if (aboutData.images.length === 1) {
      return (
        <div className="slider-container">
          <div className="slide active">
            <img
              src={aboutData.images[0]}
              alt="About us"
              className="slider-image"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = "flex";
                }
              }}
            />
            <div className="showcase-placeholder" style={{ display: "none" }}>
              <div className="placeholder-content">
                <div className="placeholder-icon">📸</div>
                <p>Image failed to load</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="slider-container">
        {aboutData.images.map((image, index) => (
          <div
            key={index}
            className={`slide ${activeSlide === index ? "active" : ""}`}
          >
            <img
              src={image}
              alt={`About us ${index + 1}`}
              className="slider-image"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = "flex";
                }
              }}
            />
            <div className="showcase-placeholder" style={{ display: "none" }}>
              <div className="placeholder-content">
                <div className="placeholder-icon">📸</div>
                <p>Image failed to load</p>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation arrows */}
        <button
          className="slider-arrow slider-arrow-prev"
          onClick={handlePrevSlide}
          aria-label="Previous image"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>

        <button
          className="slider-arrow slider-arrow-next"
          onClick={handleNextSlide}
          aria-label="Next image"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>

        {/* Dots navigation */}
        <div className="showcase-dots">
          {aboutData.images.map((_, index) => (
            <div
              key={index}
              className={`dot ${activeSlide === index ? "active" : ""}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section className="intro-section" id="about">
        <div className="container">
          <div className="intro-content">
            <div className="intro-text">
              <div className="loading-placeholder">
                <div className="loading-spinner"></div>
                <p>Loading about section...</p>
              </div>
            </div>
            <div className="intro-showcase">
              <div className="showcase-placeholder">
                <div className="loading-spinner"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  console.log("About data state:", aboutData);
  console.log("Loading state:", loading);

  return (
    <section className="intro-section" id="about">
      <div className="container">
        <div className="intro-content">
          <div className="intro-text" data-aos="fade-up" data-aos-delay="200">
            <h2>{aboutData.title}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: parseDraftContent(aboutData.description),
              }}
            />
          </div>
          <div
            className="intro-showcase"
            data-aos="fade-left"
            data-aos-delay="400"
          >
            {renderImageSlider()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroSection;
